import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  AlertCircle,
  Building2,
  CheckCircle2,
  Clock,
  ExternalLink,
  FileSignature,
  Layers,
  Loader2,
  MapPin,
  RefreshCw,
  Search,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RouterOutputs, useTRPC, useTRPCClient } from "@/trpc";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { Label } from "@/components/ui/label";
import { z } from "zod";
import { Switch } from "@/components/ui/switch";
import { Select as ReactSelect } from "@/components/Selects/select";
import { SingleValue } from "react-select";
import { ExternalLink as ExternalLinkComponent } from "@/components/Button/ExternalLink";

type JobPostStatus = "pending" | "all";

type SortOption = "alphabetical" | "createdAt" | "recentlyInterested";

type FilterSelectOption = {
  value: string;
  label: string;
};
type AdminJobPostSummary =
  RouterOutputs["jobPost"]["adminGetAll"]["jobs"][number];
type JobPostTag = {
  id?: string | null;
  name: string;
};

const ALL_ORGANIZATIONS_VALUE = "all-organizations";
const ALL_BUSINESS_UNITS_VALUE = "all-business-units";
const ALL_ORGANIZATIONS_LABEL = "All organizations";
const ALL_BUSINESS_UNITS_LABEL = "All business units";

type PotentialCandidateSearchParams = {
  lat: string;
  lng: string;
  cityName: string;
  specialty: string;
  specialties: string;
  distance: string;
  shiftDuration: string;
  stateLicenses: string;
  hasImlcLicense: string;
  professionalTypeId: string;
  professionalTypeName: string;
  professionalTypes: string;
};

const emptyPotentialCandidateSearchParams: PotentialCandidateSearchParams = {
  lat: "",
  lng: "",
  cityName: "",
  specialty: "",
  specialties: "",
  distance: "",
  shiftDuration: "",
  stateLicenses: "",
  hasImlcLicense: "",
  professionalTypeId: "",
  professionalTypeName: "",
  professionalTypes: "",
};

const PAGE_SIZE = 100;

const AdminJobPostsPage = () => {
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const [activeStatus, setActiveStatus] = useState<JobPostStatus>(
    (search.status as JobPostStatus | undefined) ?? "all",
  );
  const [localSearchTerm, setLocalSearchTerm] = useState(
    search.searchTerm ?? "",
  );
  const [showDrafts, setShowDrafts] = useState<boolean>(
    activeStatus === "all" ? (search.showDrafts ?? false) : false,
  );
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>(
    search.organizationId ?? ALL_ORGANIZATIONS_VALUE,
  );
  const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useState<string>(
    search.organizationBusinessUnitId ?? ALL_BUSINESS_UNITS_VALUE,
  );

  // Debounce for search input
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const nextStatus = (search.status as JobPostStatus | undefined) ?? "all";
    if (nextStatus !== activeStatus) {
      setActiveStatus(nextStatus);
    }

    const nextShowDrafts =
      nextStatus === "all" ? (search.showDrafts ?? false) : false;
    if (nextShowDrafts !== showDrafts) {
      setShowDrafts(nextShowDrafts);
    }

    const nextOrganizationId = search.organizationId ?? ALL_ORGANIZATIONS_VALUE;
    if (nextOrganizationId !== selectedOrganizationId) {
      setSelectedOrganizationId(nextOrganizationId);
    }

    const nextBusinessUnitId =
      nextOrganizationId === ALL_ORGANIZATIONS_VALUE
        ? ALL_BUSINESS_UNITS_VALUE
        : (search.organizationBusinessUnitId ?? ALL_BUSINESS_UNITS_VALUE);
    if (nextBusinessUnitId !== selectedBusinessUnitId) {
      setSelectedBusinessUnitId(nextBusinessUnitId);
    }

    // Sync search term from URL
    const nextSearchTerm = search.searchTerm ?? "";
    if (nextSearchTerm !== localSearchTerm) {
      setLocalSearchTerm(nextSearchTerm);
    }
  }, [
    search.status,
    search.showDrafts,
    search.organizationId,
    search.organizationBusinessUnitId,
    search.searchTerm,
    activeStatus,
    showDrafts,
    selectedOrganizationId,
    selectedBusinessUnitId,
    localSearchTerm,
  ]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const applySearchParams = useCallback(
    (
      overrides?: Partial<{
        sortBy: SortOption;
        status: JobPostStatus;
        showDrafts: boolean;
        organizationId: string | undefined;
        organizationBusinessUnitId: string | undefined;
        searchTerm: string | undefined;
      }>,
    ) => {
      const hasOverride = (key: string) =>
        overrides
          ? Object.prototype.hasOwnProperty.call(overrides, key)
          : false;

      const nextSortBy = overrides?.sortBy ?? search.sortBy;
      const nextStatus = overrides?.status ?? activeStatus;

      const nextOrganizationId = hasOverride("organizationId")
        ? overrides?.organizationId
        : selectedOrganizationId === ALL_ORGANIZATIONS_VALUE
          ? undefined
          : selectedOrganizationId;

      const nextBusinessUnitId = hasOverride("organizationBusinessUnitId")
        ? overrides?.organizationBusinessUnitId
        : selectedBusinessUnitId === ALL_BUSINESS_UNITS_VALUE
          ? undefined
          : selectedBusinessUnitId;

      const nextShowDrafts =
        nextStatus === "all"
          ? hasOverride("showDrafts")
            ? (overrides?.showDrafts ?? false)
            : showDrafts
          : false;

      const nextSearchTerm = hasOverride("searchTerm")
        ? overrides?.searchTerm
        : search.searchTerm;

      const nextSearch: Record<string, unknown> = {
        sortBy: nextSortBy,
      };

      if (nextStatus !== "all") {
        nextSearch.status = nextStatus;
      }

      if (nextShowDrafts) {
        nextSearch.showDrafts = true;
      }

      if (nextOrganizationId) {
        nextSearch.organizationId = nextOrganizationId;
      }

      if (nextBusinessUnitId) {
        nextSearch.organizationBusinessUnitId = nextBusinessUnitId;
      }

      if (nextSearchTerm) {
        nextSearch.searchTerm = nextSearchTerm;
      }

      navigate({
        to: "/admin/job-posts",
        search: () => nextSearch,
      });
    },
    [
      activeStatus,
      navigate,
      search.sortBy,
      search.searchTerm,
      selectedBusinessUnitId,
      selectedOrganizationId,
      showDrafts,
    ],
  );

  const { data: organizationsData, isLoading: organizationsLoading } = useQuery(
    trpc.organization.getOrganizations.queryOptions(),
  );

  const shouldFetchBusinessUnits =
    selectedOrganizationId !== ALL_ORGANIZATIONS_VALUE;

  const { data: businessUnitsData, isLoading: businessUnitsLoading } = useQuery(
    {
      ...trpc.organization.getBusinessUnits.queryOptions({
        orgId:
          selectedOrganizationId === ALL_ORGANIZATIONS_VALUE
            ? ""
            : selectedOrganizationId,
      }),
      enabled: shouldFetchBusinessUnits,
    },
  );

  const organizationOptions = React.useMemo<FilterSelectOption[]>(() => {
    const base: FilterSelectOption[] = [
      { value: ALL_ORGANIZATIONS_VALUE, label: ALL_ORGANIZATIONS_LABEL },
    ];

    if (!organizationsData) {
      return base;
    }

    return [
      ...base,
      ...organizationsData.map((org) => ({
        value: org.id,
        label: org.name,
      })),
    ];
  }, [organizationsData]);

  const selectedOrganizationOption = React.useMemo(() => {
    return (
      organizationOptions.find(
        (option) => option.value === selectedOrganizationId,
      ) ?? organizationOptions[0]
    );
  }, [organizationOptions, selectedOrganizationId]);

  const businessUnitOptions = React.useMemo<FilterSelectOption[]>(() => {
    const base: FilterSelectOption[] = [
      { value: ALL_BUSINESS_UNITS_VALUE, label: ALL_BUSINESS_UNITS_LABEL },
    ];

    if (!shouldFetchBusinessUnits || !businessUnitsData) {
      return base;
    }

    return [
      ...base,
      ...businessUnitsData.map((unit) => ({
        value: unit.id,
        label: unit.name,
      })),
    ];
  }, [businessUnitsData, shouldFetchBusinessUnits]);

  const selectedBusinessUnitOption = React.useMemo(() => {
    return (
      businessUnitOptions.find(
        (option) => option.value === selectedBusinessUnitId,
      ) ?? businessUnitOptions[0]
    );
  }, [businessUnitOptions, selectedBusinessUnitId]);

  React.useEffect(() => {
    if (selectedOrganizationId === ALL_ORGANIZATIONS_VALUE) {
      return;
    }

    if (organizationsLoading || !organizationsData) {
      return;
    }

    const organizationExists = organizationsData.some(
      (org) => org.id === selectedOrganizationId,
    );

    if (!organizationExists) {
      setSelectedOrganizationId(ALL_ORGANIZATIONS_VALUE);
      setSelectedBusinessUnitId(ALL_BUSINESS_UNITS_VALUE);
      applySearchParams({
        organizationId: undefined,
        organizationBusinessUnitId: undefined,
      });
    }
  }, [
    applySearchParams,
    organizationsData,
    organizationsLoading,
    selectedOrganizationId,
  ]);

  React.useEffect(() => {
    if (
      selectedBusinessUnitId === ALL_BUSINESS_UNITS_VALUE ||
      !shouldFetchBusinessUnits
    ) {
      return;
    }

    if (businessUnitsLoading || !businessUnitsData) {
      return;
    }

    const businessUnitExists = businessUnitsData.some(
      (unit) => unit.id === selectedBusinessUnitId,
    );

    if (!businessUnitExists) {
      setSelectedBusinessUnitId(ALL_BUSINESS_UNITS_VALUE);
      applySearchParams({
        organizationBusinessUnitId: undefined,
      });
    }
  }, [
    applySearchParams,
    businessUnitsData,
    businessUnitsLoading,
    selectedBusinessUnitId,
    shouldFetchBusinessUnits,
  ]);

  const handleOrganizationFilterChange = (
    option: SingleValue<FilterSelectOption>,
  ) => {
    const value = option?.value ?? ALL_ORGANIZATIONS_VALUE;
    setSelectedOrganizationId(value);
    setSelectedBusinessUnitId(ALL_BUSINESS_UNITS_VALUE);
    const organizationIdValue =
      value === ALL_ORGANIZATIONS_VALUE ? undefined : value;
    applySearchParams({
      organizationId: organizationIdValue,
      organizationBusinessUnitId: undefined,
    });
  };

  const handleBusinessUnitFilterChange = (
    option: SingleValue<FilterSelectOption>,
  ) => {
    const value = option?.value ?? ALL_BUSINESS_UNITS_VALUE;
    setSelectedBusinessUnitId(value);
    const businessUnitIdValue =
      value === ALL_BUSINESS_UNITS_VALUE ? undefined : value;
    applySearchParams({
      organizationBusinessUnitId: businessUnitIdValue,
    });
  };

  const handleStatusChange = (value: JobPostStatus) => {
    setActiveStatus(value);
    const nextShowDrafts = value === "all" ? showDrafts : false;
    if (value !== "all" && showDrafts) {
      setShowDrafts(false);
    }
    applySearchParams({
      status: value,
      showDrafts: nextShowDrafts,
    });
  };

  const handleShowDraftsToggle = (checked: boolean) => {
    const nextValue = activeStatus === "all" ? checked : false;
    setShowDrafts(nextValue);
    applySearchParams({
      showDrafts: nextValue,
    });
  };

  const handleSearchTermChange = useCallback(
    (value: string) => {
      // Update local state immediately for responsive UI
      setLocalSearchTerm(value);

      // Debounce the URL navigation to reduce API calls
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        applySearchParams({
          searchTerm: value || undefined,
        });
      }, 300);
    },
    [applySearchParams],
  );

  const organizationIdFilter =
    selectedOrganizationId === ALL_ORGANIZATIONS_VALUE
      ? undefined
      : selectedOrganizationId;

  const businessUnitIdFilter =
    selectedBusinessUnitId === ALL_BUSINESS_UNITS_VALUE
      ? undefined
      : selectedBusinessUnitId;

  // Infinite query for job posts with server-side filtering
  const {
    data: infiniteData,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
  } = useInfiniteQuery({
    queryKey: [
      "jobPost.adminGetAll",
      {
        status: activeStatus === "all" ? undefined : activeStatus,
        includeDrafts: activeStatus === "all" ? showDrafts : false,
        sortBy: search.sortBy,
        organizationId: organizationIdFilter,
        organizationBusinessUnitId: businessUnitIdFilter,
        searchTerm: search.searchTerm,
      },
    ],
    queryFn: ({ pageParam = 0 }) =>
      trpcClient.jobPost.adminGetAll.query({
        status: activeStatus === "all" ? undefined : activeStatus,
        includeDrafts: activeStatus === "all" ? showDrafts : false,
        sortBy: search.sortBy,
        organizationId: organizationIdFilter,
        organizationBusinessUnitId: businessUnitIdFilter,
        searchTerm: search.searchTerm,
        offset: pageParam * PAGE_SIZE,
        limit: PAGE_SIZE,
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.hasMore) {
        return allPages.length;
      }
      return undefined;
    },
    initialPageParam: 0,
  });

  // Flatten paginated data
  const jobPosts = useMemo(() => {
    return infiniteData?.pages.flatMap((page) => page.jobs) ?? [];
  }, [infiniteData]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSortChange = (value: SortOption) => {
    applySearchParams({ sortBy: value });
  };

  const formatDate = (value?: string | null) => {
    if (!value) {
      return null;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusBadge = (job: AdminJobPostSummary) => {
    if (!job.publishedAt) {
      return (
        <Badge className="bg-gray-100 text-gray-700 border-gray-300">
          <FileSignature className="h-3 w-3 mr-1" />
          Draft
        </Badge>
      );
    }

    if (!job.reviewedAt) {
      return (
        <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300">
          <Clock className="h-3 w-3 mr-1" />
          Pending Review
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-green-100 text-green-700 border-green-300">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Approved
        </Badge>
      );
    }
  };

  const renderJobCard = (job: AdminJobPostSummary) => {
    const professionalTypes: JobPostTag[] =
      job.healthcareProfessionalTypes ?? [];
    const specialties: JobPostTag[] = job.specialties ?? [];
    const organizationName =
      job.organization?.name ||
      job.organizationBusinessUnit?.name ||
      "Unknown organization";
    const businessUnitName = job.organizationBusinessUnit?.name || null;

    const facilityLabel = job.facilityName || job.facility?.name || null;
    const locationLabel = job.city
      ? job.stateCode
        ? `${job.city}, ${job.stateCode}`
        : job.city
      : job.stateCode || null;

    type MetaItem = {
      key: string;
      icon: React.ReactNode;
      label: string;
      muted?: boolean;
    };

    const metaItems: MetaItem[] = [];
    if (locationLabel) {
      metaItems.push({
        key: "location",
        icon: <MapPin className="h-3.5 w-3.5 text-slate-400" />,
        label: locationLabel,
      });
    }

    metaItems.push({
      key: "organization",
      icon: <Building2 className="h-3.5 w-3.5 text-slate-400" />,
      label: organizationName,
    });

    if (businessUnitName) {
      metaItems.push({
        key: "business-unit",
        icon: <Layers className="h-3.5 w-3.5 text-slate-400" />,
        label: businessUnitName,
        muted: true,
      });
    }

    const externalUrl = job.externalUrl;
    const interestedCount = job.interestedCandidates?.length ?? 0;
    const potentialCandidateCount = job.potentialCandidateCount ?? 0;
    const publishedOn = formatDate(job.publishedAt);
    const reviewedOn = formatDate(job.reviewedAt);

    return (
      <Card
        key={job.id}
        className="rounded-xl border border-slate-200/80 bg-white shadow-sm transition-all hover:border-blue-200/70 hover:shadow-lg"
      >
        <CardContent className="p-4 md:p-5">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <Link
                to="/admin/job-posts/$jobPostId"
                params={{ jobPostId: job.id }}
                search={() => emptyPotentialCandidateSearchParams}
                className="text-base font-semibold text-slate-900 hover:text-blue-600 md:text-lg"
              >
                {job.title || "Untitled Job"}
              </Link>
              <div className="flex flex-wrap items-center gap-2">
                <Badge
                  className={`text-xs font-medium ${
                    job.open
                      ? "bg-blue-50 text-blue-700 border-blue-100"
                      : "bg-slate-100 text-slate-600 border-slate-200"
                  }`}
                >
                  {job.open ? "Open" : "Closed"}
                </Badge>
                {getStatusBadge(job)}
              </div>
            </div>
            {facilityLabel && (
              <p className="text-sm text-slate-500">{facilityLabel}</p>
            )}
            {externalUrl && (
              <ExternalLinkComponent
                href={externalUrl}
                className="inline-flex items-center gap-1.5 text-sm text-amber-600 hover:text-amber-700"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                {externalUrl}
              </ExternalLinkComponent>
            )}
            {metaItems.length > 0 && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] leading-6 font-medium text-slate-600">
                {metaItems.map((meta) => (
                  <div
                    key={`${job.id}-${meta.key}`}
                    className={`flex items-center gap-1.5 ${
                      meta.muted ? "text-slate-500" : "text-slate-600"
                    }`}
                  >
                    {meta.icon}
                    <span className="truncate">{meta.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {(professionalTypes.length > 0 || specialties.length > 0) && (
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-3 text-xs leading-6 text-slate-600">
              {professionalTypes.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <Users className="h-4 w-4 text-slate-400" />
                  <div className="flex flex-wrap gap-1.5">
                    {professionalTypes.map((type) => (
                      <Badge
                        key={type.id ?? type.name}
                        variant="outline"
                        className="rounded-full border-sky-100 bg-sky-50 text-sky-800"
                      >
                        {type.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {specialties.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <Layers className="h-4 w-4 text-slate-400" />
                  <div className="flex flex-wrap gap-1.5">
                    {specialties.map((specialty) => (
                      <Badge
                        key={specialty.id ?? specialty.name}
                        variant="outline"
                        className="rounded-full border-emerald-100 bg-emerald-50 text-emerald-800"
                      >
                        {specialty.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {(interestedCount > 0 ||
            potentialCandidateCount > 0 ||
            publishedOn ||
            reviewedOn) && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-slate-100 mt-2 pt-3 text-xs text-slate-500">
              {interestedCount > 0 && (
                <div className="flex items-center gap-1.5 text-sm text-slate-600">
                  <Users className="h-4 w-4 text-slate-400" />
                  <span className="font-semibold text-slate-900">
                    {interestedCount}
                  </span>
                  <span className="text-xs uppercase tracking-wide">
                    Interested
                  </span>
                </div>
              )}
              {potentialCandidateCount > 0 && (
                <div className="flex items-center gap-1.5 text-sm text-slate-600">
                  <UserPlus className="h-4 w-4 text-slate-400" />
                  <span className="font-semibold text-slate-900">
                    {potentialCandidateCount}
                  </span>
                  <span className="text-xs uppercase tracking-wide">
                    Candidates
                  </span>
                </div>
              )}
              {publishedOn && (
                <div className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4 text-slate-400" />
                  <span>Published {publishedOn}</span>
                </div>
              )}
              {reviewedOn && (
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-slate-400" />
                  <span>Reviewed {reviewedOn}</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Job Post Management</h1>
        <Button
          onClick={() => refetch()}
          variant="outline"
          disabled={isLoading}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>

      {/* Search Bar, Status Filter, and Sort */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mb-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search by job title or facility name..."
              value={localSearchTerm}
              onChange={(e) => handleSearchTermChange(e.target.value)}
              className="pl-10 pr-10"
            />
            {localSearchTerm && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSearchTermChange("")}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-gray-100"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div className="w-full sm:w-64">
            <ReactSelect<FilterSelectOption, false>
              instanceId="admin-job-posts-organization-select"
              value={selectedOrganizationOption}
              onChange={handleOrganizationFilterChange}
              options={organizationOptions}
              isSearchable
              isClearable={false}
              isLoading={organizationsLoading}
              classNamePrefix="react-select"
              placeholder={ALL_ORGANIZATIONS_LABEL}
            />
          </div>
          <div className="w-full sm:w-64">
            <ReactSelect<FilterSelectOption, false>
              instanceId="admin-job-posts-business-unit-select"
              value={selectedBusinessUnitOption}
              onChange={handleBusinessUnitFilterChange}
              options={businessUnitOptions}
              isSearchable
              isClearable={false}
              isDisabled={!shouldFetchBusinessUnits}
              isLoading={shouldFetchBusinessUnits && businessUnitsLoading}
              classNamePrefix="react-select"
              placeholder={ALL_BUSINESS_UNITS_LABEL}
              noOptionsMessage={() =>
                shouldFetchBusinessUnits
                  ? "No business units found"
                  : "Select an organization first"
              }
            />
          </div>
          <Select value={activeStatus} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-full sm:w-40 h-10">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Select value={search.sortBy} onValueChange={handleSortChange}>
            <SelectTrigger className="w-full sm:w-48 h-10">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alphabetical">Alphabetical</SelectItem>
              <SelectItem value="createdAt">Created Date</SelectItem>
              <SelectItem value="recentlyInterested">
                Recently Interested
              </SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center justify-between sm:justify-center gap-2 rounded-md border border-gray-200 px-3 py-2 w-full sm:w-auto">
            <Switch
              id="show-drafts"
              checked={showDrafts}
              onCheckedChange={handleShowDraftsToggle}
              disabled={activeStatus !== "all"}
            />
            <Label
              htmlFor="show-drafts"
              className={`text-sm ${
                activeStatus !== "all" ? "text-gray-400" : "text-gray-700"
              }`}
            >
              Show drafts
            </Label>
          </div>
        </div>
        {search.searchTerm && (
          <p className="text-sm text-gray-600">
            Showing results for "{search.searchTerm}"
          </p>
        )}
      </div>

      <div className="space-y-4">
        {isLoading ? (
          <Card className="border-dashed">
            <CardContent className="p-8 md:p-12 flex flex-col items-center justify-center text-center">
              <div className="bg-blue-100 p-3 rounded-full mb-4">
                <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Loading job posts</h3>
              <p className="text-gray-600 mb-6 max-w-md">
                Please wait while we fetch the job posts for review.
              </p>
            </CardContent>
          </Card>
        ) : isError ? (
          <Card className="border-dashed border-2 border-red-300 bg-red-50">
            <CardContent className="p-8 md:p-12 flex flex-col items-center justify-center text-center">
              <div className="bg-red-100 p-3 rounded-full mb-4">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">
                Failed to load job posts
              </h3>
              <p className="text-gray-600 mb-6 max-w-md">
                {error instanceof Error
                  ? error.message
                  : "An error occurred while loading job posts. Please try again."}
              </p>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => refetch()}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : jobPosts && jobPosts.length > 0 ? (
          <>
            {/* Show subtle loading indicator when refetching due to filter changes */}
            {isFetching && !isFetchingNextPage && (
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Updating results...</span>
              </div>
            )}
            {jobPosts.map(renderJobCard)}

            {/* Infinite scroll trigger */}
            {hasNextPage && (
              <div
                ref={loadMoreRef}
                className="flex items-center justify-center py-6"
              >
                {isFetchingNextPage ? (
                  <div className="flex items-center space-x-2 text-slate-500">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span className="text-sm font-medium">
                      Loading more job posts...
                    </span>
                  </div>
                ) : (
                  <div className="h-4" />
                )}
              </div>
            )}
          </>
        ) : (
          <Card className="border-dashed border-2 border-gray-300 bg-gray-50">
            <CardContent className="p-8 md:p-12 flex flex-col items-center justify-center text-center">
              <div className="bg-blue-100 p-3 rounded-full mb-4">
                <FileSignature className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">
                {activeStatus === "pending"
                  ? "No pending job posts"
                  : "No job posts found"}
              </h3>
              <p className="text-gray-600 max-w-md">
                {activeStatus === "pending"
                  ? "All job posts have been reviewed."
                  : "No job posts match the current filter."}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export const Route = createFileRoute("/_admin/admin/job-posts/")({
  component: AdminJobPostsPage,
  validateSearch: z.object({
    sortBy: z
      .enum(["alphabetical", "createdAt", "recentlyInterested"])
      .optional()
      .default("createdAt"),
    status: z.enum(["pending", "all"]).optional().default("all"),
    organizationId: z.string().uuid().optional(),
    organizationBusinessUnitId: z.string().uuid().optional(),
    showDrafts: z.boolean().optional().default(false),
    searchTerm: z.string().optional(),
  }),
});
